import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { EventBusyRounded, WarningAmberRounded } from "@mui/icons-material";
import type { SupplierCapacityAlertLevel } from "../../../types/model/valueObject/location/SupplierLocationListingVO";
import { formatDateMMDDyyyy } from "../../../util/DateUtils";

interface LocationCardCapacityAlertBadgeProps {
  /** Today's alert level; `NONE` or missing renders no threshold pill. */
  alertLevel?: SupplierCapacityAlertLevel | null;
  /** Booked spaces as a whole percent of today's capacity. */
  utilizationPercent?: number | null;
  /** `yyyy-MM-dd` end date of a capacity schedule block that ends tomorrow. */
  scheduleEndsOn?: string | null;
  /** Capacity that applies once the schedule block ends. */
  capacityAfterScheduleEnds?: number | null;
}

type Tone = "warning" | "critical" | "info";

const TONES: Record<Tone, { background: string; color: string; border: string }> = {
  warning: { background: "#fff6e0", color: "#9a6700", border: "1px solid #f3d38a" },
  critical: { background: "#fde8e8", color: "#c04040", border: "1px solid #f5b8b8" },
  info: { background: "#e8f1fb", color: "#2b6cb0", border: "1px solid #b7d3f2" },
};

const Pill = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: Tone }>(({ tone }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.7857rem",  // 11px at 14px root
  fontWeight: 700,
  padding: "2px 8px",
  borderRadius: 3,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  // Wraps inside narrow field columns (phone, and the 4-up grid at lg) rather
  // than spilling into the next field.
  maxWidth: "100%",
  lineHeight: 1.3,
  ...TONES[tone],
}));

const Stack = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  marginTop: 4,
  maxWidth: "100%",
});

const ICON_SX = { fontSize: "0.8571rem", flexShrink: 0 };

const SubLabel = styled(Box)({
  fontSize: "0.7857rem",  // 11px at 14px root
  color: "#5E7782",
  marginTop: 2,
});

const thresholdLabel = (alertLevel: SupplierCapacityAlertLevel, utilizationPercent?: number | null) => {
  const floor = alertLevel === "HIGH" ? 90 : 80;
  return utilizationPercent != null ? `${utilizationPercent}% Booked` : `${floor}%+ Booked`;
};

/**
 * Capacity alert pills for GMS PRO supplier location cards (#3250).
 *
 * - Amber "80% Booked" / red "90% Booked" when today's bookings cross a capacity threshold.
 * - Blue "Schedule Ends Tomorrow" when a capacity schedule block ends tomorrow, with the end date
 *   and the capacity that applies afterwards as visible text (no hover-only details).
 *
 * Renders nothing when there's nothing to flag, so it can be dropped into any card unconditionally.
 */
const LocationCardCapacityAlertBadge = ({
  alertLevel,
  utilizationPercent,
  scheduleEndsOn,
  capacityAfterScheduleEnds,
}: LocationCardCapacityAlertBadgeProps) => {
  const showThreshold = alertLevel === "MEDIUM" || alertLevel === "HIGH";
  const showScheduleEnding = !!scheduleEndsOn;
  if (!showThreshold && !showScheduleEnding) {
    return null;
  }

  const scheduleDetail = scheduleEndsOn
    ? `Ends ${formatDateMMDDyyyy(scheduleEndsOn)}`
      + (capacityAfterScheduleEnds != null ? `, then ${capacityAfterScheduleEnds} spaces` : "")
    : "";

  return (
    <Stack>
      {showThreshold && (
        <Pill tone={alertLevel === "HIGH" ? "critical" : "warning"} data-testid="capacity-threshold-badge">
          <WarningAmberRounded sx={ICON_SX} aria-hidden="true" />
          {thresholdLabel(alertLevel, utilizationPercent)}
        </Pill>
      )}
      {showScheduleEnding && (
        <Box sx={{ maxWidth: "100%" }}>
          <Pill tone="info" data-testid="capacity-schedule-ending-badge">
            <EventBusyRounded sx={ICON_SX} aria-hidden="true" />
            Schedule Ends Tomorrow
          </Pill>
          <SubLabel data-testid="capacity-schedule-ending-detail">{scheduleDetail}</SubLabel>
        </Box>
      )}
    </Stack>
  );
};

export default LocationCardCapacityAlertBadge;
